import clsx from 'clsx';
import * as React from 'react';

import {
    type AutocompleteLiteralValueSuggestion,
    type AutocompleteOperatorSuggestion,
    type AutocompleteSuggestions,
    type AutocompleteSymbolSuggestion,
    type ExpressionRuntime,
    type ExtractSymbolDef,
    parseTemplate,
    type SymbolsTable,
    SymbolType,
} from '@gitbook/expr';
import { useListKeyboardNavigation } from '@gitbook/react-uikit';
import {
    AtomButton,
    Description,
    Group,
    type IconDefinition,
    KeyboardShortcut,
    Link,
    renderIcon,
} from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { type ExpressionContextAutocompleteFilter, useExpressionContext } from '../context';

const AutocompleteDecorationContext = React.createContext<string>('');

type SuggestionItem = {
    /** Icon to display in the suggestion list. */
    icon?: IconDefinition;

    /** Suggestion data */
    suggestion: AutocompleteSuggestions[number];

    /** Text to display as a suggestion. */
    value: string;

    /** Text to actually insert when the suggestion is selected. */
    insertText: string;

    /** Whether the suggestion is filtered. */
    isFiltered?: ReturnType<ExpressionContextAutocompleteFilter>;
};

/**
 * Hook to get the current autocomplete suggestion.
 */
export function useAutocompleteDecoration() {
    return React.useContext(AutocompleteDecorationContext);
}

export interface WithExpressionAutocompleteProps {
    /**
     * Value of the expression.
     */
    value: string;
    /**
     * Cursor offset.
     */
    cursorOffset: number;
    /**
     * Whether the editor using the autocomplete is focused.
     */
    isEditorFocused: boolean;
    /**
     * Callback to call when the user inserts text.
     */
    onInsertText: (text: string) => void;
    /**
     * Editing mode of the editor:
     * - expression: the expression can include both dynamic & deterministic properties.
     * - deterministic-expression: the expression can *only* include deterministic (non-dynamic) properties.
     * - template: the expression is a template expression.
     */
    mode?: 'expression' | 'deterministic-expression' | 'template';
}

/**
 * Editor for an expression.
 */
export function WithExpressionAutocomplete(
    props: React.PropsWithChildren<WithExpressionAutocompleteProps>
) {
    const {
        value,
        cursorOffset,
        isEditorFocused,
        onInsertText,
        children,
        mode = 'expression',
    } = props;

    const suggestionsItemRefs = React.useRef<Record<string, HTMLButtonElement>>({});

    const { runtime, symbolsTable, autocompleteFilter } = useExpressionContext();
    const suggestions = React.useMemo(() => {
        const textAfterCursor = value.slice(cursorOffset);

        // We don't autocompletion if the cursor is in the middle of a word
        if (textAfterCursor.length > 0 && !textAfterCursor.startsWith(' ')) {
            return [];
        }

        const suggestions = getAutocompleteSuggestions({
            expression: value,
            cursorOffset,
            runtime,
            symbolsTable,
            mode,
        });

        return suggestions
            .map((suggestion) => {
                const item = getSuggestionItem(suggestion);
                const isFiltered =
                    item && item.suggestion.type === 'symbol' && autocompleteFilter
                        ? autocompleteFilter(item.suggestion.symbol.ref)
                        : undefined;
                return item
                    ? {
                          ...item,
                          ...(item.suggestion.type === 'symbol' && autocompleteFilter
                              ? {
                                    isFiltered,
                                }
                              : {}),
                      }
                    : null;
            })
            .filter(filterOutNullable);
    }, [value, cursorOffset, runtime, symbolsTable, autocompleteFilter, mode]);

    const onSubmitSuggestion = (suggestion: SuggestionItem) => {
        const textUntilCursor = value.slice(0, cursorOffset);
        const suffix = getAutocompleteSuffix(textUntilCursor, suggestion.insertText);

        if (mode === 'deterministic-expression' && !!suggestion.isFiltered) {
            return;
        }

        if (suffix.length > 0) {
            onInsertText(suffix);
        }
    };

    const list = useListKeyboardNavigation({
        items: suggestions,
        startAtFirst: true,
        active: isEditorFocused,
        scrollTo: (suggestion) => {
            const ref = suggestionsItemRefs.current[suggestion.value];
            if (ref) {
                ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        },
        onSelect: onSubmitSuggestion,
        getKey: (suggestion) => suggestion.value,
        submit: 'Tab',
        lateralNavigation: false,
    });

    const decoration = React.useMemo(() => {
        if (!list.cursor.current?.insertText) {
            return '';
        }

        const textUntilCursor = value.slice(0, cursorOffset);
        return getAutocompleteSuffix(textUntilCursor, list.cursor.current?.insertText ?? '');
    }, [value, cursorOffset, list.cursor.current?.insertText]);

    return (
        <div className="flex flex-col">
            <AutocompleteDecorationContext.Provider value={decoration}>
                {children}
            </AutocompleteDecorationContext.Provider>
            {list.cursor.current ? (
                <div className="flex flex-row border-base border-t bg-muted">
                    <div className="flex max-h-48 basis-44 flex-col overflow-y-auto p-1">
                        {suggestions.map((entry) => {
                            const { value } = entry;
                            const isActive = list.cursor.current === entry;
                            const isFilteredInDeterministicMode =
                                mode === 'deterministic-expression' && !!entry.isFiltered;

                            return (
                                <AtomButton
                                    key={value}
                                    onPress={
                                        !isFilteredInDeterministicMode
                                            ? () => {
                                                  onSubmitSuggestion(entry);
                                              }
                                            : // Prevent suggestion to be inserted for dynamic properties in deterministic mode.
                                              undefined
                                    }
                                    className={clsx(
                                        'flex items-center gap-2 rounded px-3 py-1.5 text-start ',
                                        isActive ? 'bg-standout-side bg-opacity-45 text-base' : null
                                    )}
                                    onMouseEnter={() => list.hoverItem(entry)}
                                    ref={(ref) => {
                                        if (ref) {
                                            suggestionsItemRefs.current[value] = ref;
                                        }
                                    }}
                                >
                                    {entry.icon
                                        ? renderIcon(entry.icon, {
                                              className: 'size-4 text-muted',
                                          })
                                        : null}
                                    <div
                                        className={clsx(
                                            'flex-1 text-ellipsis text-secondary text-ui-action',
                                            isFilteredInDeterministicMode && 'opacity-disabled'
                                        )}
                                    >
                                        {value}
                                    </div>
                                    {isActive && !isFilteredInDeterministicMode ? (
                                        <KeyboardShortcut shortcut="Tab" />
                                    ) : null}
                                </AtomButton>
                            );
                        })}
                    </div>
                    <div className="max-h-48 flex-1 overflow-y-auto border-base border-l">
                        <SuggestionInfoHoverCardBody
                            mode={mode}
                            suggestion={list.cursor.current.suggestion}
                            isFiltered={list.cursor.current.isFiltered}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function getSuggestionItem(suggestion: AutocompleteSuggestions[number]): SuggestionItem | null {
    const value =
        suggestion.type === 'symbol'
            ? suggestion.symbol.definition.name
            : suggestion.type === 'operator'
              ? suggestion.operator
              : suggestion.value.kind === 'direct'
                ? String(
                      suggestion.value.type === SymbolType.String
                          ? `"${suggestion.value.data}"`
                          : suggestion.value.data
                  )
                : undefined;

    if (!value) {
        return null;
    }

    let insertText = value;
    if (suggestion.type === 'operator') {
        insertText = ` ${value} `;
    }

    if (suggestion.type === 'symbol' && suggestion.symbol.definition.type === SymbolType.Object) {
        insertText = `${value}.`;
    }

    return {
        suggestion,
        value,
        insertText,
    };
}

function getAutocompleteSuggestions(args: {
    expression: string;
    cursorOffset: number;
    runtime: ExpressionRuntime;
    symbolsTable: SymbolsTable;
    mode: 'expression' | 'deterministic-expression' | 'template';
}): AutocompleteSuggestions {
    const { expression, cursorOffset, runtime, symbolsTable, mode } = args;

    if (mode === 'template') {
        const parts = parseTemplate(expression);
        const exprPart = parts.find(
            (p) => p.type === 'expression' && cursorOffset >= p.start && cursorOffset <= p.end
        );
        if (!exprPart || exprPart.type !== 'expression') {
            return [];
        }

        return getExpressionAutocompleteSuggestions({
            expression: exprPart.value,
            cursorOffset: cursorOffset - exprPart.start,
            runtime,
            symbolsTable,
        });
    }

    return getExpressionAutocompleteSuggestions({
        expression,
        cursorOffset,
        runtime,
        symbolsTable,
    });
}

function getExpressionAutocompleteSuggestions(args: {
    expression: string;
    cursorOffset: number;
    runtime: ExpressionRuntime;
    symbolsTable: SymbolsTable;
}): AutocompleteSuggestions {
    const { expression, cursorOffset, runtime, symbolsTable } = args;

    if (expression.length === 0) {
        const candidatesKeys = symbolsTable.getMatchingSymbolsKeys(['*']);
        return candidatesKeys.reduce<AutocompleteSuggestions>((previous, candidate) => {
            const symbolInfo = symbolsTable.getSymbolInfo(candidate);
            if (symbolInfo) {
                previous.push({ type: 'symbol', symbol: symbolInfo });
            }
            return previous;
        }, []);
    }

    const results = runtime.autocomplete(expression, cursorOffset, symbolsTable);
    return results.suggestions;
}

function SuggestionInfoHoverCardBody(props: {
    mode: WithExpressionAutocompleteProps['mode'];
    suggestion:
        | AutocompleteSymbolSuggestion
        | AutocompleteOperatorSuggestion
        | AutocompleteLiteralValueSuggestion;
    isFiltered?: SuggestionItem['isFiltered'];
}) {
    const { mode, suggestion, isFiltered } = props;
    if (suggestion.type === 'operator') {
        return (
            <SuggestionInfoCard
                header={
                    <p>
                        <strong className="mr-2 text-ui-heading-small">({suggestion.type})</strong>
                        {suggestion.description}
                    </p>
                }
                link={suggestion.link}
            />
        );
    }

    if (suggestion.type === 'literal-value') {
        return suggestion.value.kind === 'direct' ? (
            <SuggestionInfoCard
                header={
                    <p>
                        <strong className="mr-2 text-ui-heading-small">(literal)</strong>
                        <code className="break-words">
                            {String(suggestion.value.data)}: {suggestion.value.type}
                        </code>
                    </p>
                }
            />
        ) : /** TODO: show suggestion card for in-array literal values */ null;
    }

    const { symbol } = suggestion;
    const symbolKind =
        symbol.definition.type === SymbolType.Function
            ? symbol.definition.type
            : symbol.parentRef
              ? 'property'
              : 'object';
    const isFilteredInDeterministicMode = mode === 'deterministic-expression' && !!isFiltered;

    return !isFilteredInDeterministicMode ? (
        <SuggestionInfoCard
            header={
                <p>
                    <strong className="mr-2 text-ui-heading-small">({symbolKind})</strong>
                    <code className="break-words">{getSymbolTypeInfo(symbol.definition)}</code>
                </p>
            }
            documentation={symbol.definition.description}
            link={symbol.definition.link}
        />
    ) : (
        <div className="flex size-full flex-row items-center justify-center p-2">
            <Description>
                {typeof isFiltered === 'object'
                    ? isFiltered.exclusionMessage
                    : 'This property is filtered.'}
            </Description>
        </div>
    );
}

function SuggestionInfoCard(props: {
    header: React.ReactNode;
    documentation?: string;
    link?: string;
    warning?: string;
}) {
    const { header, documentation, link } = props;
    return (
        <div>
            <Group orientation="horizontal" className="w-full p-2">
                {header}
            </Group>
            {(documentation || link) && (
                <div className="flex w-full flex-grow flex-col flex-wrap items-stretch border-t p-2">
                    {documentation && (
                        <Description className="self-start">{documentation}</Description>
                    )}
                    {link && (
                        <Link className="self-end" href={link} target="_blank">
                            Reference
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

function getSymbolTypeInfo(
    symbolDef: ExtractSymbolDef<SymbolType>,
    includeSymbolName = true
): string {
    if (symbolDef.type === SymbolType.Function) {
        const funcArgs = symbolDef.args
            .map((arg) => `${arg.name}${arg.optional ? '?' : ''}: ${getSymbolTypeInfo(arg, false)}`)
            .join(', ');
        return symbolDef.name
            ? `${symbolDef.name}(${funcArgs}): ${getSymbolTypeInfo(symbolDef.returns, false)}`
            : `(${funcArgs}) => ${getSymbolTypeInfo(symbolDef.returns, false)}`;
    }
    if (symbolDef.type === SymbolType.Union) {
        return symbolDef.members.map((member) => getSymbolTypeInfo(member)).join(' | ');
    }
    if (symbolDef.type === SymbolType.Array) {
        return `${symbolDef.name ? `${symbolDef.name}: ` : ''}${getSymbolTypeInfo(symbolDef.items)}[]`;
    }

    return `${symbolDef.name && includeSymbolName ? `${symbolDef.name}: ` : ''}${symbolDef.type}`;
}

/**
 * Returns the minimal suffix that must be appended to `inputText`
 * so that the result equals `inputText + suffix === suggestion`.
 *
 * If `inputText` already contains the whole suggestion, an empty
 * string is returned.
 */
export function getAutocompleteSuffix(inputText: string, suggestion: string): string {
    // Fast path – suggestion already complete or shorter
    if (inputText === suggestion || suggestion.length === 0) return '';

    // The maximum possible overlap is bounded by the shorter string
    const maxOverlap = Math.min(inputText.length, suggestion.length);

    // Walk downward from the largest possible overlap
    for (let k = maxOverlap; k > 0; k--) {
        // Slice the last k characters of inputText
        const overlap = inputText.slice(-k);

        // If those k characters match the first k characters of suggestion,
        // we found the longest valid overlap.
        if (suggestion.startsWith(overlap)) {
            return suggestion.slice(k);
        }
    }

    // No overlap at all → return the full suggestion
    return suggestion;
}
